import { useEffect, useMemo, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

function EmployeeProspects() {
  const navigate = useNavigate();

  const [employee, setEmployee] =
    useState(null);

  const [prospects, setProspects] =
    useState([]);

  const [
    temperatureFilter,
    setTemperatureFilter,
  ] = useState("Warm");

  const [
    selectedProspect,
    setSelectedProspect,
  ] = useState(null);

  const [
    temperature,
    setTemperature,
  ] = useState("Warm");

  const [stage, setStage] =
    useState("New Prospect");

  const [
    nextFollowUpDate,
    setNextFollowUpDate,
  ] = useState("");

  const [
    nextFollowUpTime,
    setNextFollowUpTime,
  ] = useState("");

  const [remark, setRemark] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const todayDate = new Date()
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem(
        "crmEmployee"
      );

    if (!savedEmployee) {
      navigate(
        "/employee-login"
      );
      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(
          savedEmployee
        );
    } catch (error) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate(
        "/employee-login"
      );
      return;
    }

    if (
      !parsedEmployee?.email
    ) {
      navigate(
        "/employee-login"
      );
      return;
    }

    setEmployee(
      parsedEmployee
    );

    const loadProspects =
      async () => {
        try {
          setLoading(true);
          setMessage("");

          const employeeEmail =
            parsedEmployee.email
              .trim()
              .toLowerCase();

          const prospectQuery =
            query(
              collection(
                db,
                "prospects"
              ),
              where(
                "assignedEmployeeEmail",
                "==",
                employeeEmail
              )
            );

          const snapshot =
            await getDocs(
              prospectQuery
            );

          const list =
            snapshot.docs.map(
              (
                documentItem
              ) => ({
                id:
                  documentItem.id,

                ...documentItem.data(),
              })
            );

          setProspects(
            list
          );
        } catch (error) {
          console.error(
            "Prospect loading error:",
            error
          );

          setMessage(
            "Unable to load prospects."
          );
        } finally {
          setLoading(false);
        }
      };

    loadProspects();
  }, [navigate]);

  const getTimeValue = (
    value
  ) => {
    if (!value) {
      return 0;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value
          .toDate()
          .getTime();
      }

      const date =
        new Date(value);

      return Number.isNaN(
        date.getTime()
      )
        ? 0
        : date.getTime();
    } catch (error) {
      return 0;
    }
  };

  const filteredProspects =
    useMemo(() => {
      return prospects
        .filter(
          (
            prospect
          ) =>
            (
              prospect.temperature ||
              prospect.prospectTemperature ||
              "Warm"
            ) ===
            temperatureFilter
        )
        .sort(
          (
            a,
            b
          ) => {
            /*
              IMPORTANT:

              We use lastWorkedAt only.

              updatedAt can be created by
              system operations or when the
              prospect was first created.

              Only an actual employee prospect
              update should send it downward.
            */

            const aWorked =
              getTimeValue(
                a.lastWorkedAt
              );

            const bWorked =
              getTimeValue(
                b.lastWorkedAt
              );

            /*
              Fresh prospects stay above
              already-worked prospects.
            */

            if (
              !aWorked &&
              bWorked
            ) {
              return -1;
            }

            if (
              aWorked &&
              !bWorked
            ) {
              return 1;
            }

            /*
              Both fresh:
              newest fresh prospect first.
            */

            if (
              !aWorked &&
              !bWorked
            ) {
              return (
                getTimeValue(
                  b.createdAt
                ) -
                getTimeValue(
                  a.createdAt
                )
              );
            }

            /*
              Both already worked:

              Oldest worked remains above.

              Most recently worked prospect
              goes furthest down.
            */

            return (
              aWorked -
              bWorked
            );
          }
        );
    }, [
      prospects,
      temperatureFilter,
    ]);

  const openProspect = (
    prospect
  ) => {
    setSelectedProspect(
      prospect
    );

    setTemperature(
      prospect.temperature ||
        prospect.prospectTemperature ||
        "Warm"
    );

    setStage(
      prospect.stage ||
        prospect.prospectStage ||
        "New Prospect"
    );

    setNextFollowUpDate(
      prospect.nextFollowUpDate ||
        ""
    );

    setNextFollowUpTime(
      prospect.nextFollowUpTime ||
        ""
    );

    setRemark("");
    setMessage("");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const saveProspect =
    async () => {
      if (
        !selectedProspect
      ) {
        return;
      }

      if (
        stage !==
          "Converted" &&
        stage !==
          "Lost" &&
        (
          !nextFollowUpDate ||
          !nextFollowUpTime
        )
      ) {
        setMessage(
          "Next follow-up date and time are required."
        );

        return;
      }

      if (
        !remark.trim()
      ) {
        setMessage(
          "Enter a short discussion/update."
        );

        return;
      }

      try {
        setSaving(true);
        setMessage("");

        const activityTime =
          new Date()
            .toISOString();

        const historyEntry = {
          employeeId:
            employee?.id ||
            "",

          employeeName:
            employee?.name ||
            "Employee",

          employeeEmail:
            employee?.email ||
            "",

          temperature,

          stage,

          remark:
            remark.trim(),

          nextFollowUpDate:
            stage ===
              "Converted" ||
            stage ===
              "Lost"
              ? ""
              : nextFollowUpDate,

          nextFollowUpTime:
            stage ===
              "Converted" ||
            stage ===
              "Lost"
              ? ""
              : nextFollowUpTime,

          createdAt:
            activityTime,
        };

        const prospectUpdate = {
          temperature,

          prospectTemperature:
            temperature,

          stage,

          prospectStage:
            stage,

          latestRemark:
            remark.trim(),

          /*
            This is the dedicated field
            used for sorting.
          */
          lastWorkedAt:
            activityTime,

          nextFollowUpDate:
            stage ===
              "Converted" ||
            stage ===
              "Lost"
              ? ""
              : nextFollowUpDate,

          nextFollowUpTime:
            stage ===
              "Converted" ||
            stage ===
              "Lost"
              ? ""
              : nextFollowUpTime,

          remarksHistory:
            arrayUnion(
              historyEntry
            ),

          updatedAt:
            serverTimestamp(),
        };

        await updateDoc(
          doc(
            db,
            "prospects",
            selectedProspect.id
          ),
          prospectUpdate
        );

        const updatedProspect = {
          ...selectedProspect,

          ...prospectUpdate,

          lastWorkedAt:
            activityTime,

          updatedAt:
            activityTime,
        };

        setProspects(
          (
            currentProspects
          ) =>
            currentProspects.map(
              (
                prospect
              ) =>
                prospect.id ===
                selectedProspect.id
                  ? updatedProspect
                  : prospect
            )
        );

        setSelectedProspect(
          updatedProspect
        );

        setRemark("");

        setMessage(
          "Prospect updated successfully."
        );
      } catch (error) {
        console.error(
          "Prospect update error:",
          error
        );

        setMessage(
          "Unable to save prospect."
        );
      } finally {
        setSaving(false);
      }
    };

  const getTemperatureClass = (
    prospectTemperature
  ) => {
    if (
      prospectTemperature ===
      "Hot"
    ) {
      return "converted";
    }

    if (
      prospectTemperature ===
      "Warm"
    ) {
      return "callback";
    }

    return "pending";
  };

  if (
    selectedProspect
  ) {
    return (
      <div className="employee-calls-page">

        <header className="mobile-crm-header">

          <div>

            <h1>
              Trading Wealth CRM
            </h1>

            <p>
              Prospect Update
            </p>

          </div>

          <button
            type="button"
            className="mobile-back-button"
            onClick={() => {
              setSelectedProspect(
                null
              );

              setMessage("");
            }}
          >
            Back
          </button>

        </header>

        <main className="employee-calls-main">

          {message && (

            <div className="attendance-message">
              {message}
            </div>

          )}

          <section className="customer-call-card">

            <div>

              <p className="small-label">
                PROSPECT
              </p>

              <h2>
                {selectedProspect.customerName ||
                  "Customer"}
              </h2>

              <p>
                {selectedProspect.phone ||
                  "-"}
              </p>

            </div>

            <a
              className="call-customer-button"
              href={`tel:${
                selectedProspect.phone ||
                ""
              }`}
            >
              Call Prospect
            </a>

          </section>

          <section className="lead-information-grid">

            <div>

              <span>
                Current Temperature
              </span>

              <strong>
                {selectedProspect.temperature ||
                  selectedProspect.prospectTemperature ||
                  "Warm"}
              </strong>

            </div>

            <div>

              <span>
                Current Stage
              </span>

              <strong>
                {selectedProspect.stage ||
                  selectedProspect.prospectStage ||
                  "New Prospect"}
              </strong>

            </div>

            <div>

              <span>
                Next Follow-up
              </span>

              <strong>
                {selectedProspect.nextFollowUpDate ||
                  "-"}{" "}
                {selectedProspect.nextFollowUpTime ||
                  ""}
              </strong>

            </div>

            <div>

              <span>
                Priority
              </span>

              <strong>
                {selectedProspect.priority ||
                  "Normal"}
              </strong>

            </div>

          </section>

          <section className="employee-update-card">

            <h3>
              Update Prospect
            </h3>

            <div className="form-group">

              <label>
                Prospect Temperature
              </label>

              <select
                value={
                  temperature
                }
                onChange={(
                  event
                ) =>
                  setTemperature(
                    event.target.value
                  )
                }
              >

                <option value="Cold">
                  Cold
                </option>

                <option value="Warm">
                  Warm
                </option>

                <option value="Hot">
                  Hot
                </option>

              </select>

            </div>

            <div className="form-group">

              <label>
                Prospect Stage
              </label>

              <select
                value={
                  stage
                }
                onChange={(
                  event
                ) =>
                  setStage(
                    event.target.value
                  )
                }
              >

                <option value="New Prospect">
                  New Prospect
                </option>

                <option value="Requirement Understood">
                  Requirement Understood
                </option>

                <option value="Plan Discussed">
                  Plan Discussed
                </option>

                <option value="Documents Pending">
                  Documents Pending
                </option>

                <option value="Payment Pending">
                  Payment Pending
                </option>

                <option value="Ready for Onboarding">
                  Ready for Onboarding
                </option>

                <option value="Converted">
                  Converted
                </option>

                <option value="Lost">
                  Lost
                </option>

              </select>

            </div>

            {stage !==
              "Converted" &&
              stage !==
                "Lost" && (

                <div className="callback-fields-grid">

                  <div className="form-group">

                    <label>
                      Next Follow-up Date *
                    </label>

                    <input
                      type="date"
                      min={
                        todayDate
                      }
                      value={
                        nextFollowUpDate
                      }
                      onChange={(
                        event
                      ) =>
                        setNextFollowUpDate(
                          event.target.value
                        )
                      }
                    />

                  </div>

                  <div className="form-group">

                    <label>
                      Next Follow-up Time *
                    </label>

                    <input
                      type="time"
                      value={
                        nextFollowUpTime
                      }
                      onChange={(
                        event
                      ) =>
                        setNextFollowUpTime(
                          event.target.value
                        )
                      }
                    />

                  </div>

                </div>

              )}

            <div className="form-group">

              <label>
                Complete Discussion in Short *
              </label>

              <textarea
                className="crm-textarea"
                value={
                  remark
                }
                onChange={(
                  event
                ) =>
                  setRemark(
                    event.target.value
                  )
                }
                placeholder="Write customer discussion and next action"
              />

            </div>

            <button
              type="button"
              className="save-call-update-button"
              onClick={
                saveProspect
              }
              disabled={
                saving
              }
            >
              {saving
                ? "Saving..."
                : "Save Prospect Update"}
            </button>

          </section>

          <section className="remark-history-card">

            <h3>
              Previous Prospect Remarks
            </h3>

            {!Array.isArray(
              selectedProspect.remarksHistory
            ) ||
            selectedProspect.remarksHistory
              .length ===
              0 ? (

              <p className="no-remarks-text">
                No previous remarks.
              </p>

            ) : (

              <div className="remark-timeline">

                {[
                  ...selectedProspect.remarksHistory,
                ]
                  .reverse()
                  .map(
                    (
                      historyItem,
                      index
                    ) => (

                      <div
                        key={
                          `${historyItem.createdAt}-${index}`
                        }
                        className="remark-history-item"
                      >

                        <div className="remark-history-top">

                          <strong>
                            {historyItem.employeeName ||
                              "Employee"}
                          </strong>

                          <span>
                            {historyItem.createdAt
                              ? new Date(
                                  historyItem.createdAt
                                ).toLocaleString()
                              : "-"}
                          </span>

                        </div>

                        <p>

                          <strong>
                            {historyItem.temperature ||
                              ""}{" "}

                            {historyItem.stage
                              ? `• ${historyItem.stage}`
                              : ""}
                          </strong>

                        </p>

                        <p>
                          {historyItem.remark ||
                            historyItem.discussion ||
                            "-"}
                        </p>

                        {historyItem.nextFollowUpDate && (

                          <small>

                            Next Follow-up:{" "}
                            {
                              historyItem.nextFollowUpDate
                            }{" "}
                            {
                              historyItem.nextFollowUpTime
                            }

                          </small>

                        )}

                      </div>

                    )
                  )}

              </div>

            )}

          </section>

        </main>

      </div>
    );
  }

  return (
    <div className="employee-calls-page">

      <header className="mobile-crm-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            My Prospects
          </p>

        </div>

        <button
          type="button"
          className="mobile-back-button"
          onClick={() =>
            navigate(
              "/employee-calls"
            )
          }
        >
          My Calls
        </button>

      </header>

      <main className="employee-calls-main">

        <section className="employee-calls-heading">

          <p className="small-label">
            PROSPECT PIPELINE
          </p>

          <h2>
            {employee?.name ||
              "Employee"}
            ’s Prospects
          </h2>

          <p>
            Fresh prospects stay at the top.
            After an update, that prospect moves
            below the remaining fresh records.
          </p>

        </section>

        {message && (

          <div className="attendance-message">
            {message}
          </div>

        )}

        <div className="employee-filter-tabs">

          {[
            "Cold",
            "Warm",
            "Hot",
          ].map(
            (
              temperatureItem
            ) => (

              <button
                key={
                  temperatureItem
                }
                type="button"
                className={
                  temperatureFilter ===
                  temperatureItem
                    ? "employee-filter-button active"
                    : "employee-filter-button"
                }
                onClick={() =>
                  setTemperatureFilter(
                    temperatureItem
                  )
                }
              >
                {temperatureItem}
              </button>

            )
          )}

        </div>

        {loading ? (

          <div className="employee-empty-state">
            Loading prospects...
          </div>

        ) : filteredProspects.length ===
          0 ? (

          <div className="employee-empty-state">
            No {temperatureFilter} prospects.
          </div>

        ) : (

          <div className="mobile-lead-list">

            {filteredProspects.map(
              (
                prospect
              ) => {
                const prospectTemperature =
                  prospect.temperature ||
                  prospect.prospectTemperature ||
                  "Warm";

                return (

                  <article
                    key={
                      prospect.id
                    }
                    className="mobile-lead-card"
                  >

                    <div className="mobile-lead-card-top">

                      <div>

                        <h3>
                          {prospect.customerName ||
                            "Customer"}
                        </h3>

                        <p>
                          {prospect.phone ||
                            "-"}
                        </p>

                      </div>

                      <span
                        className={`mobile-status ${getTemperatureClass(
                          prospectTemperature
                        )}`}
                      >
                        {
                          prospectTemperature
                        }
                      </span>

                    </div>

                    <div className="mobile-lead-meta">

                      <span>
                        Stage:{" "}
                        {prospect.stage ||
                          prospect.prospectStage ||
                          "New Prospect"}
                      </span>

                      <span>
                        Follow-up:{" "}
                        {prospect.nextFollowUpDate ||
                          "-"}{" "}
                        {prospect.nextFollowUpTime ||
                          ""}
                      </span>

                    </div>

                    {prospect.latestRemark && (

                      <p className="latest-remark-preview">
                        Latest:{" "}
                        {
                          prospect.latestRemark
                        }
                      </p>

                    )}

                    <div className="mobile-lead-actions">

                      <a
                        className="small-call-button"
                        href={`tel:${
                          prospect.phone ||
                          ""
                        }`}
                      >
                        Call
                      </a>

                      <button
                        type="button"
                        className="open-lead-button"
                        onClick={() =>
                          openProspect(
                            prospect
                          )
                        }
                      >
                        Open & Update
                      </button>

                    </div>

                  </article>

                );
              }
            )}

          </div>

        )}

      </main>

    </div>
  );
}

export default EmployeeProspects;